import apis from '#store/api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DataApiResponse, DataCollectionApiResponse } from './types';

const HOT_WASH_KEY = 'hotWash';

export type HotWashType = 'Strength' | 'Improvement';

export interface HotWash {
    id?: string;
    name?: string;
    type?: HotWashType;
    incidentId?: string;
}

interface GetHotWashParams {
    skip?: number;
    take: number;
    own: boolean;
    type: 'Strength' | 'Improvement';
    incidentId?: string;
}

interface GetHotWashByIdParams {
    id: string;
}

interface CreateHotWashParams {
    hotWash: Omit<HotWash, 'id'>;
}

interface UpdateHotWashParams {
    hotWash: HotWash;
}

interface BatchDeleteHotWashParams {
    ids: string[];
}

// Fetch hot wash list
export const useHotWash = (params: GetHotWashParams) => {
    return useQuery({
        queryKey: [HOT_WASH_KEY, params],
        queryFn: async () => {
            const response = await apis.hotWash.getHotWashList({ params });
            return response.data as DataCollectionApiResponse<HotWash>;
        },
        enabled: !!params.incidentId
    });
};

// Fetch hot wash by id
export const useHotWashById = (params: GetHotWashByIdParams) => {
    return useQuery({
        queryKey: [HOT_WASH_KEY, params.id],
        queryFn: async () => {
            const response = await apis.hotWash.getHotWashById({ params: { id: params.id } });
            return response.data as DataApiResponse<HotWash>;
        },
    });
};

// Create chief complaint
export const useCreateHotWash = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (params: CreateHotWashParams) => {
            const response = await apis.hotWash.createHotWash({ data: params });
            return response.data as DataApiResponse<HotWash>;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [HOT_WASH_KEY] });
        },
    });
};

// Update hot wash
export const useUpdateHotWash = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (params: UpdateHotWashParams) => {
            const response = await apis.hotWash.updateHotWash({ data: params });
            return response.data as DataApiResponse<HotWash>;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [HOT_WASH_KEY] });
        },
    });
};

// Batch delete hot wash
export const useBatchDeleteHotWash = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (params: BatchDeleteHotWashParams) => {
            const response = await apis.hotWash.batchDeleteHotWash({ data: params });
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [HOT_WASH_KEY] });
        }
    });
};